import { useMemo, useState, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { Bot, ChevronDown, ChevronRight, Folder, Loader2, Plus } from "lucide-react";
import { defaultBrowserMount } from "@/api/views";

import { AgentDropdown } from "@/components/agent-dropdown";
import {
  SidebarPinSortable,
  SidebarPinSortableRow,
} from "@/components/sidebar-pin-sortable";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { Agent } from "@/hooks/use-agents";
import { useIsMobile } from "@/hooks/use-media-query";
import { useResizableHeight } from "@/hooks/use-resizable-height";
import {
  mergePinOrder,
  type SidebarPinOrderingApi,
} from "@/hooks/use-sidebar-pin-ordering";
import { cn, type AccessTokenGetter } from "@/lib/utils";

export interface AgentSidebarSectionProps {
  agents: Agent[];
  /** Raw, server-ordered pin IDs. Inaccessible/stale IDs stay here but hide. */
  pins: string[];
  pinsLoading: boolean;
  pinsError: string | null;
  /** RBAC-filtered catalog state used to resolve raw pin IDs safely. */
  agentsLoading?: boolean;
  agentsError?: string | null;
  defaultPinInitializing?: boolean;
  getToken: AccessTokenGetter;
  activeAgentId?: string | null;
  /** Config-marked agents promoted above the user's persisted pin order. */
  pinnedByDefaultAgentIds?: readonly string[];
  /** Drag-and-drop display ordering for the pinned rows. */
  pinOrdering?: SidebarPinOrderingApi;
  onOpenAgent: (agentId: string) => void;
  onCreateConversation: (agentId: string) => void | Promise<void>;
  onRetry?: () => void | Promise<void>;
  onRetryAgents?: () => void | Promise<void>;
}

const AGENTS_EXPANDED_STORAGE_KEY = "sidebar-agents-expanded";
const AGENTS_RESIZE_OPTIONS = {
  initial: 208,
  min: 64,
  max: 480,
  minPeer: 240,
  storageKey: "sidebar-agents-height",
  handleSide: "top",
} as const;

/**
 * Compact, server-backed pinned Agents collection. It only maps already
 * authorized `useAgents` entries and deliberately leaves inaccessible raw
 * pin IDs untouched in persistence for a future RBAC change.
 */
export function AgentSidebarSection({
  agents,
  pins,
  pinsLoading,
  pinsError,
  agentsLoading = false,
  agentsError = null,
  defaultPinInitializing = false,
  getToken,
  activeAgentId,
  pinnedByDefaultAgentIds = [],
  pinOrdering,
  onOpenAgent,
  onCreateConversation,
  onRetry,
  onRetryAgents,
}: AgentSidebarSectionProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const resize = useResizableHeight(AGENTS_RESIZE_OPTIONS);
  const [expanded, setExpanded] = useState(() => {
    if (typeof window !== "undefined") {
      const saved = window.localStorage.getItem(AGENTS_EXPANDED_STORAGE_KEY);
      if (saved === "1" || saved === "true") return true;
      if (saved === "0" || saved === "false") return false;
    }
    return true;
  });
  const [creatingAgentId, setCreatingAgentId] = useState<string | null>(null);
  const individualAgents = useMemo(
    () => agents.filter((agent) => agent.type === "agent"),
    [agents],
  );
  const displayPins = pinOrdering
    ? pinOrdering.displayIds(pins, "agentIds", pinnedByDefaultAgentIds)
    : mergePinOrder(pins, null, pinnedByDefaultAgentIds);
  const visiblePinnedAgents = useMemo(() => {
    const byId = new Map(individualAgents.map((agent) => [agent.id, agent]));
    return displayPins.flatMap((agentId) => {
      const agent = byId.get(agentId);
      return agent ? [agent] : [];
    });
  }, [individualAgents, displayPins]);

  const handleCreate = async (event: MouseEvent, agentId: string) => {
    event.stopPropagation();
    if (creatingAgentId) return;
    setCreatingAgentId(agentId);
    try {
      await onCreateConversation(agentId);
    } finally {
      setCreatingAgentId(null);
    }
  };

  const isResolving = pinsLoading || defaultPinInitializing;

  const header = (
    <div className="flex items-center justify-between gap-2 pl-1 pr-3 py-1 text-xs font-medium text-muted-foreground">
      <div className="flex min-w-0 items-center gap-1">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className="size-6 shrink-0"
                data-testid="sidebar-agents-toggle"
                aria-expanded={expanded}
                aria-label={expanded ? t("sidebar.collapseAgents") : t("sidebar.expandAgents")}
                onClick={() => setExpanded((current) => {
                  const next = !current;
                  try {
                    window.localStorage.setItem(AGENTS_EXPANDED_STORAGE_KEY, String(next));
                  } catch {
                    /* localStorage may be unavailable (private mode) — ignore */
                  }
                  return next;
                })}
              >
                {expanded ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">
              {expanded ? t("sidebar.collapseAgents") : t("sidebar.expandAgents")}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <h3 className="flex min-w-0 items-center px-1 text-left text-xs font-medium text-muted-foreground" id="sidebar-agents-heading">
          <span className="truncate">{t("sidebar.agents")}</span>
        </h3>
      </div>
      <AgentDropdown
        agents={individualAgents}
        currentAgentId={null}
        loading={agentsLoading}
        error={agentsError}
        getToken={getToken}
        onSelect={onOpenAgent}
        onCreateSession={onCreateConversation}
        ariaLabel={t("sidebar.openAgents")}
        trigger={(
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            data-testid="sidebar-agents-view"
            className="size-5 p-0"
            aria-label={t("sidebar.openAgents")}
            title={t("sidebar.openAgents")}
          >
            <Bot className="size-3.5" />
          </Button>
        )}
      />
    </div>
  );

  return (
    <section
      id="sidebar-agents-section"
      tabIndex={-1}
      className="relative shrink-0 border-t pl-2 py-2 focus-visible:outline-none"
      aria-label={t("sidebar.agents")}
    >
      {expanded ? (
        <div
          className="flex min-h-0 flex-col"
          style={{ maxHeight: resize.height }}
          data-testid="sidebar-agents-resizable-content"
        >
          <div
            role="separator"
            aria-orientation="horizontal"
            aria-label={t("sidebar.resizeAgentsSection")}
            onPointerDown={resize.onPointerDown}
            onPointerMove={resize.onPointerMove}
            onPointerUp={resize.onPointerUp}
            onDoubleClick={resize.onDoubleClick}
            className="absolute inset-x-0 -top-1.5 z-10 h-3 cursor-row-resize touch-none select-none"
            data-testid="sidebar-agents-resize-handle"
          />
          {header}
          <div className="min-h-0 flex-1 overflow-y-auto pr-2 pb-1">
            {isResolving || agentsLoading ? (
              <div className="flex items-center gap-2 px-2 py-2 text-xs text-muted-foreground">
                <Loader2 className="size-3.5 animate-spin" />
                {t("common:status.loading")}
              </div>
            ) : pinsError ? (
              <div className="flex items-center justify-between gap-2 px-2 py-2 text-xs text-destructive">
                <span>{t("sidebar.pinnedAgentsLoadFailed")}</span>
                {onRetry && (
                  <Button type="button" variant="ghost" size="sm" className="h-6 px-1.5 text-xs" onClick={() => { void onRetry(); }}>
                    {t("common:actions.retry")}
                  </Button>
                )}
              </div>
            ) : agentsError ? (
              <div className="flex items-center justify-between gap-2 px-2 py-2 text-xs text-destructive">
                <span>{t("agent.errorLoading")}</span>
                {onRetryAgents && (
                  <Button type="button" variant="ghost" size="sm" className="h-6 px-1.5 text-xs" onClick={() => { void onRetryAgents(); }}>
                    {t("common:actions.retry")}
                  </Button>
                )}
              </div>
            ) : visiblePinnedAgents.length === 0 ? (
              <p className="px-2 py-2 text-xs text-muted-foreground">{t("sidebar.noPinnedAgents")}</p>
            ) : (
              <SidebarPinSortable
                ids={displayPins}
                onReorder={(orderedIds) => pinOrdering?.reorder("agentIds", orderedIds)}
                className="space-y-0.5"
                aria-labelledby="sidebar-agents-heading"
              >
                {visiblePinnedAgents.map((agent) => {
                  const active = activeAgentId === agent.id;
                  const filesMount = defaultBrowserMount(agent.views);
                  const controlsClassName = cn(
                    isMobile
                      ? "ml-auto flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 focus-within:opacity-100"
                      : "pointer-events-none flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 focus-within:opacity-100",
                    isMobile && filesMount && "opacity-100 pointer-events-auto",
                  );
                  return (
                    <SidebarPinSortableRow
                      key={agent.id}
                      id={agent.id}
                      testId={`sidebar-agent-${agent.id}`}
                      actionsOverlayClassName="bg-gradient-to-l from-background via-background/80 to-transparent pl-4"
                      className={cn(
                        active ? "bg-secondary text-secondary-foreground" : "hover:bg-muted/60",
                      )}
                      actions={(
                        <div className={controlsClassName}>
                          {filesMount && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon-sm"
                              className={cn(!isMobile && "pointer-events-auto")}
                              data-testid={`sidebar-agent-files-${agent.id}`}
                              aria-label={t("sidebar.openFilesForAgent", { name: agent.name })}
                              title={t("sidebar.openFilesForAgent", { name: agent.name })}
                              onClick={(event) => {
                                event.stopPropagation();
                                const owner = `/agent/${encodeURIComponent(agent.id)}/conversations`;
                                const params = new URLSearchParams();
                                params.set("panel", `agent:${filesMount.id}`);
                                params.set("filesMount", filesMount.mount_id!);
                                params.set("filesMountFor", owner);
                                window.history.pushState(window.history.state, "", `${owner}?${params}`);
                                window.dispatchEvent(new PopStateEvent("popstate"));
                              }}
                            >
                              <Folder className="size-3.5" />
                            </Button>
                          )}
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon-sm"
                                  data-testid={`sidebar-agent-create-${agent.id}`}
                                  disabled={creatingAgentId === agent.id}
                                  aria-label={t("sidebar.createConversationWithAgent", { name: agent.name })}
                                  onClick={(event) => { void handleCreate(event, agent.id); }}
                                  className={cn(!isMobile && "pointer-events-auto")}
                                >
                                  {creatingAgentId === agent.id
                                    ? <Loader2 className="size-3.5 animate-spin" />
                                    : <Plus className="size-3.5" />}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="right">
                                {t("sidebar.createConversationWithAgent", { name: agent.name })}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      )}
                    >
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => onOpenAgent(agent.id)}
                        className="h-auto min-w-0 flex-1 justify-start rounded-sm py-1 pl-1 pr-0 text-left font-normal hover:bg-transparent"
                        aria-current={active ? "page" : undefined}
                        aria-label={`${t("agent.conversations")}: ${agent.name}`}
                      >
                        <span className="truncate">{agent.name}</span>
                      </Button>
                    </SidebarPinSortableRow>
                  );
                })}
              </SidebarPinSortable>
            )}
          </div>
        </div>
      ) : header}
    </section>
  );
}
